import { EXERCISE_BASKET } from '@src/const/sessionStorageConsts';
import { useSidebarStore } from '@stores/sidebarStore';
import { useQueryClient } from '@tanstack/vue-query';
import {
  exerciseProgramsKeys,
  exerciseProgramsQueries,
  usersKeys,
  usersQueries,
} from '@therame/api-client';
import {
  BasketViewMode,
  ExerciseFullSizeDto,
  ExercisePerformanceType,
  ExerciseProgramFullSizeDto,
  ExerciseProgramItemFullSizeDto,
  generateUUID,
  OfficeProductAssignmentFullSizeDto,
  UserFullSizeDto,
  Visibility,
} from '@therame/shared';
import { defineStore } from 'pinia';
import { Subject } from 'rxjs';
import { computed, ref, type Ref, type WritableComputedRef } from 'vue';

type ExerciseBasketStore = {
  dragIsStarted: Ref<boolean>;
  expanded: Ref<boolean>;
  user: Ref<UserFullSizeDto | undefined>;
  onPatientSelect: (newPatientId: string | undefined) => Promise<void>;
  lockPatient: Ref<boolean>;
  resetUser: () => void;
  dropPatientVideos: () => void;
  addExercise: (exercise: ExerciseFullSizeDto) => void;
  viewMode: Ref<BasketViewMode>;
  resetAll: () => void;
  hasExercise: (exerciseId: string) => boolean;
  onAddTemplateToBasket: (programItems: ExerciseProgramItemFullSizeDto[]) => void;
  addExerciseProgramItem: (exerciseProgramItem: ExerciseProgramItemFullSizeDto) => void;
  exerciseTemplate: Ref<ExerciseProgramFullSizeDto | null>;
  basket: WritableComputedRef<ExerciseProgramItemFullSizeDto[]>;
  addToBasketAnimationTrigger: Subject<DOMRect>;
  filterExercise: (exerciseId: string) => void;
  resetBasket: () => void;
  temporaryBasket: Ref<ExerciseProgramItemFullSizeDto[]>;
  selectedPatientOfficeProductAssignment: Ref<OfficeProductAssignmentFullSizeDto | null>;
};

export const useExerciseBasketStore = defineStore('exerciseBasket', (): ExerciseBasketStore => {
  const sidebarStore = useSidebarStore();
  const queryClient = useQueryClient();
  const user = ref<UserFullSizeDto | undefined>(undefined);
  const lockPatient = ref<boolean>(false);
  const expanded = ref<boolean>(false);
  const exerciseTemplate = ref<ExerciseProgramFullSizeDto | null>(null);
  const addToBasketAnimationTrigger = new Subject<DOMRect>();
  const primaryBasket = ref<ExerciseProgramItemFullSizeDto[]>([]);
  const temporaryBasket = ref<ExerciseProgramItemFullSizeDto[]>([]);
  const selectedPatientOfficeProductAssignment = ref<OfficeProductAssignmentFullSizeDto | null>(
    null,
  );

  const dragIsStarted = ref<boolean>(false);
  /**
   * The basket is just a plain array of ExerciseProgramItems.
   * When adding an exercise to an existing program, this is done in a sidebar, which has its own
   * basket (temporaryBasket) to prevent overriding the primary one.
   */
  const basket = computed<ExerciseProgramItemFullSizeDto[]>({
    get: () => {
      return sidebarStore.isSidebarOpen('exerciseLibrary')
        ? temporaryBasket.value
        : primaryBasket.value;
    },
    set: (value: ExerciseProgramItemFullSizeDto[]) => {
      if (sidebarStore.isSidebarOpen('exerciseLibrary')) {
        temporaryBasket.value = value;
      } else {
        primaryBasket.value = value;
      }
    },
  });

  const viewMode = ref<BasketViewMode>(BasketViewMode.PROGRAM);

  const hasExercise = (exerciseId: string) => {
    return basket.value.some((exerciseProgram) => exerciseProgram.exerciseId === exerciseId);
  };

  const addExerciseProgramItem = (exerciseProgramItem: ExerciseProgramItemFullSizeDto) => {
    const newExerciseProgramItem: ExerciseProgramItemFullSizeDto =
      new ExerciseProgramItemFullSizeDto();
    newExerciseProgramItem.exerciseId = exerciseProgramItem.exercise.id;
    newExerciseProgramItem.exercise = exerciseProgramItem.exercise;
    newExerciseProgramItem.performanceType = exerciseProgramItem.performanceType;
    newExerciseProgramItem.performanceContent = exerciseProgramItem.performanceContent;
    newExerciseProgramItem.setCount = exerciseProgramItem.setCount;
    newExerciseProgramItem.pauseDuration = exerciseProgramItem.pauseDuration;
    newExerciseProgramItem.weight = exerciseProgramItem.weight;
    newExerciseProgramItem.sortId = generateUUID();
    basket.value?.push(newExerciseProgramItem);
  };

  const addExercise = (exercise: ExerciseFullSizeDto) => {
    const exerciseProgramItem: ExerciseProgramItemFullSizeDto =
      new ExerciseProgramItemFullSizeDto();
    exerciseProgramItem.exerciseId = exercise.id;
    exerciseProgramItem.exercise = exercise;
    exerciseProgramItem.performanceType = ExercisePerformanceType.REPITITION;
    exerciseProgramItem.performanceContent = 1;
    exerciseProgramItem.setCount = 1;
    exerciseProgramItem.pauseDuration = 30;

    if (exercise.weightNeeded) {
      exerciseProgramItem.weight = 1000;
    }
    exerciseProgramItem.sortId = generateUUID();
    basket.value?.push(exerciseProgramItem);
  };

  const onPatientSelect = async (newPatientId: string | undefined) => {
    if (newPatientId !== user.value?.id) {
      // Order matters: `resetUser` strips the outgoing patient's videos, so it has
      // to run before the basket is associated with anybody new.
      resetUser();
      if (!newPatientId) {
        return;
      }
      user.value = await queryClient.fetchQuery({
        queryKey: usersKeys.byId(newPatientId!),
        queryFn: () => usersQueries.getById(newPatientId!),
      });
      if (user.value) {
        user.value.exercisePrograms = await queryClient.fetchQuery({
          queryKey: exerciseProgramsKeys.byUser(newPatientId!),
          queryFn: () => exerciseProgramsQueries.getByUser(newPatientId!),
        });
      }
    }
  };

  const resetUser = () => {
    lockPatient.value = false;
    dropPatientVideos();
    user.value = undefined;
  };

  /**
   * Drops every patient video from the basket.
   *
   * A patient video may only be used for the one patient it was filmed for, and
   * the client cannot tell which patient that is: `sharedWithUserId` is
   * `@Exclude()`d from `ExerciseFullSizeDto`, so it arrives `undefined` no matter
   * who is logged in. The only rule this store can honestly enforce is therefore
   * the all-or-nothing one the confirm dialog in BasketModal.vue already promises
   * the therapist: leaving the patient means leaving their videos behind.
   *
   * Filters both baskets independently rather than writing through `basket`:
   * whichever one the sidebar happens to be reading, the other outlives the
   * patient switch and would hand the outgoing patient's video to the next one.
   * Each basket keeps its own remaining items — filtering in place never copies
   * the sidebar's working set into the persisted basket.
   */
  const dropPatientVideos = () => {
    const withoutPatientVideos = (items: ExerciseProgramItemFullSizeDto[]) =>
      items.filter(
        (exerciseProgram) => exerciseProgram.exercise?.visibility !== Visibility.ONE_PATIENT,
      );

    primaryBasket.value = withoutPatientVideos(primaryBasket.value);
    temporaryBasket.value = withoutPatientVideos(temporaryBasket.value);
  };

  const filterExercise = (exerciseId: string) => {
    primaryBasket.value = basket.value.filter(
      (exerciseProgram) => exerciseProgram.exerciseId !== exerciseId,
    );
  };

  const resetBasket = () => {
    primaryBasket.value = [];
  };

  const resetAll = () => {
    resetBasket();
    // `resetBasket` only empties the primary basket, but this also detaches the
    // patient — so the sidebar's temporary basket has to give up their videos
    // too, exactly as `resetUser` does. Non-patient temporary items survive.
    dropPatientVideos();
    viewMode.value = BasketViewMode.PROGRAM;
    user.value = undefined;
    lockPatient.value = false;
    exerciseTemplate.value = null;
  };

  const onAddTemplateToBasket = (programItems: ExerciseProgramItemFullSizeDto[]) => {
    if (!basket.value) {
      return;
    }

    primaryBasket.value = basket.value.concat(programItems);
  };

  // The basket does NOT survive a reload, and that is the rule rather than an
  // omission: the tab staying open is the case that matters. It used to persist to
  // sessionStorage so a hard reload kept the assembled program, but a reloaded tab
  // then showed the count badge on the Übungskorb button for a basket nobody had
  // reopened. (Written while the basket was an Entwurf; it is no longer one —
  // ADR-0029 §H-3 — and the stale-badge reason stands on its own.)
  //
  // The key is cleared rather than merely ignored, so a session that still carries
  // one from before this change does not keep it for the life of the tab. That also
  // ends the leak the persistence had to be fixed for: the writer watched `basket`,
  // which reads the sidebar's temporary working set while that sidebar is open, so a
  // patient video `dropPatientVideos` had just filtered out went on sitting in
  // storage, ready to come back for the next patient. Nothing is written now, so
  // there is nothing to come back.
  sessionStorage.removeItem(EXERCISE_BASKET);

  const store: ExerciseBasketStore = {
    dragIsStarted,
    expanded,
    user,
    onPatientSelect,
    lockPatient,
    resetUser,
    dropPatientVideos,
    addExercise,
    viewMode,
    resetAll,
    hasExercise,
    onAddTemplateToBasket,
    addExerciseProgramItem,
    exerciseTemplate,
    basket,
    addToBasketAnimationTrigger,
    filterExercise,
    resetBasket,
    temporaryBasket,
    selectedPatientOfficeProductAssignment,
  };
  return store;
});
